import "../styles/admin-base.css";
import "../styles/event-management.css";
import {
	CalendarDays,
	MapPin,
	Clock,
	Plus,
	PencilLine,
	ExternalLink,
	X,
	AlertTriangle,
	MoreHorizontal,
	RotateCcw,
	Users,
	CheckCircle2,
	RefreshCw,
	Search,
	Filter,
	ChevronLeft,
	ChevronRight,
	Grid,
	List,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/admin";
import { API_BASE_URL } from "../../src/config";
import { resolveBackendAssetUrl } from "../../src/utils/assetUrl";
import {
	ACCEPTED_IMAGE_EXTENSIONS,
	MAX_OUTPUT_IMAGE_FILE_SIZE_BYTES,
	compressImageForUpload,
	validateInitialImageFile,
} from "../../src/utils/imageUpload";

const CATEGORY_OPTIONS = [
	{ value: "Assembly", label: "General Assembly" },
	{ value: "Training", label: "Seminar / Training" },
	{ value: "Benefit", label: "Health & Wellness" },
	{ value: "Outreach", label: "Community Outreach" },
	{ value: "Other", label: "Other" },
];

const STATUS_OPTIONS = ["Draft", "Published", "Archived"];
const ATTENDING_REGISTRATION_STATUSES = new Set(["registered", "attended"]);

const MAX_PREVIEW_FILE_SIZE = 5 * 1024 * 1024; // 5 MB limit for event thumbnails
const MAX_COMPRESSED_PREVIEW_FILE_SIZE = MAX_OUTPUT_IMAGE_FILE_SIZE_BYTES;

const CATEGORY_LABEL_LOOKUP = new Map(
	CATEGORY_OPTIONS.map((option) => [option.value, option.label]),
);

const EVENT_TABLE_COLUMN_WIDTHS = ["30%", "16%", "10%", "12%", "10%", "8%", "5%", "9%"];
const TABLE_PAGE_SIZE = 8;
const ACTION_MENU_GAP = 8;
const ACTION_MENU_VIEWPORT_PADDING = 12;
const ACTION_MENU_WIDTH = 188;
const ACTION_MENU_MIN_HEIGHT = 120;
const ACTION_MENU_PREFERRED_HEIGHT = 220;

const DEFAULT_ADMIN_ID = (() => {
	const envValue = process.env.REACT_APP_DEFAULT_ADMIN_ID;
	const parsed = envValue ? Number.parseInt(envValue, 10) : NaN;
	if (Number.isFinite(parsed) && parsed > 0) {
		return parsed;
	}
	return 1;
})();

function computeFixedMenuPosition(triggerElement, options = {}) {
	if (!triggerElement?.getBoundingClientRect) {
		return {
			top: ACTION_MENU_VIEWPORT_PADDING,
			left: ACTION_MENU_VIEWPORT_PADDING,
			maxHeight: ACTION_MENU_PREFERRED_HEIGHT,
		};
	}

	const rect = triggerElement.getBoundingClientRect();
	const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
	const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
	const menuWidth = options.menuWidth ?? ACTION_MENU_WIDTH;
	const gap = options.gap ?? ACTION_MENU_GAP;
	const padding = options.padding ?? ACTION_MENU_VIEWPORT_PADDING;
	const minHeight = options.minHeight ?? ACTION_MENU_MIN_HEIGHT;
	const preferredHeight = options.preferredHeight ?? ACTION_MENU_PREFERRED_HEIGHT;

	const safeLeft = Math.max(
		padding,
		Math.min(rect.right - menuWidth, viewportWidth - menuWidth - padding),
	);
	const spaceBelow = Math.max(0, viewportHeight - rect.bottom - gap - padding);
	const spaceAbove = Math.max(0, rect.top - gap - padding);
	const shouldOpenUp = spaceBelow < minHeight && spaceAbove > spaceBelow;
	const maxHeight = Math.max(minHeight, shouldOpenUp ? spaceAbove : spaceBelow);
	const desiredHeight = Math.min(preferredHeight, maxHeight);
	const safeTop = shouldOpenUp
		? Math.max(padding, rect.top - gap - desiredHeight)
		: Math.max(padding, Math.min(rect.bottom + gap, viewportHeight - desiredHeight - padding));

	return {
		top: safeTop,
		left: safeLeft,
		maxHeight,
	};
}

function resolvePreviewSrc(value) {
	return resolveBackendAssetUrl(value, { apiBaseUrl: API_BASE_URL });
}

function formatDateTime(value) {
	if (!value) return "—";
	const date = new Date(value);
	if (!Number.isNaN(date.getTime())) {
		return date.toLocaleString("en-PH", {
			month: "short",
			day: "2-digit",
			year: "numeric",
			hour: "numeric",
			minute: "2-digit",
		});
	}

	const fallback = String(value);
	const match = fallback.match(
		/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}))?/
	);
	if (match) {
		const [, datePart, timePart] = match;
		return timePart ? `${datePart} ${timePart}` : datePart;
	}

	return "—";
}

function toInputDate(value) {
	if (!value) return "";
	const text = String(value);
	const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
	if (match) return match[1];

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toISOString().slice(0, 10);
}

function toInputTime(value) {
	if (!value) return "";
	const text = String(value);
	const match = text.match(/(?:T|\s)(\d{2}:\d{2})/);
	if (match) return match[1];

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toISOString().slice(11, 16);
}

function combineDateTime(date, time) {
	if (!date || !time) return null;
	const trimmedTime = String(time).trim();
	if (!trimmedTime) return null;

	const amPmMatch = trimmedTime.match(/^([0-1]?\d):([0-5]\d)\s?(AM|PM)$/i);
	if (amPmMatch) {
		let hours = Number(amPmMatch[1]);
		const minutes = amPmMatch[2];
		const period = amPmMatch[3].toUpperCase();
		if (period === "PM" && hours !== 12) {
			hours += 12;
		}
		if (period === "AM" && hours === 12) {
			hours = 0;
		}
		const hoursString = String(hours).padStart(2, "0");
		return `${date} ${hoursString}:${minutes}`;
	}

	const twentyFourHourMatch = trimmedTime.match(/^([0-1]?\d|2[0-3]):([0-5]\d)$/);
	if (twentyFourHourMatch) {
		const hours = String(twentyFourHourMatch[1]).padStart(2, "0");
		const minutes = twentyFourHourMatch[2];
		return `${date} ${hours}:${minutes}`;
	}

	return `${date} ${trimmedTime}`;
}

function getStatusTone(status) {
	if (!status) return "is-blue";
	const lowered = String(status).toLowerCase();
	if (lowered === "published") return "is-blue";
	if (lowered === "draft") return "is-orange";
	if (lowered === "archived") return "is-red";
	return "is-blue";
}

function formatDateLabel(value) {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return String(value);
	}
	return date.toLocaleDateString("en-CA");
}

function formatTimeLabel(value) {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return "—";
	}
	return date.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
	});
}

function buildDescriptionPreview(value, maxLength = 120) {
	const text = String(value ?? "").trim();
	if (!text) return "No description provided yet.";
	if (text.length <= maxLength) return text;
	const shortened = text.slice(0, maxLength);
	const safe = shortened.replace(/\s+\S*$/, "");
	return `${safe.trim()}…`;
}

function normalizeAttendees(registrations) {
	if (!Array.isArray(registrations)) {
		return [];
	}

	return registrations
		.filter((registration) => {
			const status = String(registration?.status ?? "").toLowerCase();
			return ATTENDING_REGISTRATION_STATUSES.has(status);
		})
		.map((registration) => {
			const userId = Number(registration?.userId);
			const membershipNo = typeof registration?.membershipNo === "string"
				? registration.membershipNo.trim()
				: typeof registration?.membership_no === "string"
					? registration.membership_no.trim()
					: "";
			const resolvedName = String(
				registration?.userName
					?? registration?.name
					?? registration?.userEmail
					?? (Number.isFinite(userId) ? `User ${userId}` : "Unknown member"),
			).trim();

			return {
				id: registration?.id ?? null,
				userId: Number.isFinite(userId) ? userId : null,
				membershipNo: membershipNo || null,
				name: resolvedName || "Unknown member",
				status: registration?.status ?? "registered",
				registeredAt: registration?.registeredAt ?? null,
			};
		})
		.sort((a, b) => {
			const aTime = a.registeredAt ? new Date(a.registeredAt).getTime() : 0;
			const bTime = b.registeredAt ? new Date(b.registeredAt).getTime() : 0;
			return aTime - bTime;
		});
}

function buildFormStateFromEvent(event) {
	if (!event) {
		return {
			title: "",
			category: CATEGORY_OPTIONS[0]?.value ?? "Other",
			description: "",
			date: "",
			time: "",
			venue: "",
			capacity: "",
			previewImageUrl: "",
			previewImageFile: null,
			status: STATUS_OPTIONS[0],
			externalLinkEnabled: false,
			externalLink: "",
		};
	}

	return {
		title: event.title ?? "",
		category: CATEGORY_OPTIONS.some((option) => option.value === event.category)
			? event.category
			: CATEGORY_OPTIONS[0]?.value ?? "Other",
		description: event.description ?? "",
		date: toInputDate(event.startAt),
		time: toInputTime(event.startAt),
		venue: event.venue ?? "",
		capacity:
			event.capacity === null || event.capacity === undefined
				? ""
				: String(event.capacity),
		previewImageUrl: event.previewImage ?? "",
		previewImageFile: null,
		status: STATUS_OPTIONS.includes(event.status)
			? event.status
			: STATUS_OPTIONS[0],
		externalLinkEnabled: Boolean(event.externalLink),
		externalLink: event.externalLink ?? "",
	};
}

function buildServerErrorMessage(payload, fallbackMessage) {
	const baseMessage = typeof payload?.message === "string"
		? payload.message
		: fallbackMessage;
	const details = payload?.details;
	const extraParts = [];

	if (payload?.errorCode) {
		extraParts.push(`code: ${payload.errorCode}`);
	}

	if (typeof details === "string" && details.trim()) {
		extraParts.push(details.trim());
	} else if (details && typeof details === "object") {
		if (typeof details.hint === "string" && details.hint.trim()) {
			extraParts.push(details.hint.trim());
		}
		if (typeof details.reason === "string" && details.reason.trim()) {
			extraParts.push(details.reason.trim());
		}
		if (typeof details.driverCode === "string" && details.driverCode.trim()) {
			extraParts.push(`driver: ${details.driverCode.trim()}`);
		}
		if (!extraParts.length) {
			try {
				const serialized = JSON.stringify(details);
				if (serialized && serialized !== "{}") {
					extraParts.push(serialized);
				}
			} catch (_err) {
				// ignore JSON issues and avoid blocking user feedback
			}
		}
	}

	if (!extraParts.length) {
		return baseMessage;
	}

	return `${baseMessage} (${extraParts.join(" | ")})`;
}

export default function EventManagement() {
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState(null);
	const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 50, isSample: false });
	const [attendanceStats, setAttendanceStats] = useState({
		scheduled: 0,
		attended: 0,
		waitlisted: 0,
	});
	const [dialogState, setDialogState] = useState({ mode: null, event: null });
	const [formState, setFormState] = useState(buildFormStateFromEvent(null));
	const [formErrors, setFormErrors] = useState({});
	const [dialogError, setDialogError] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isImageProcessing, setIsImageProcessing] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [previewInputKey, setPreviewInputKey] = useState(0);
	const [previewState, setPreviewState] = useState({ isOpen: false, event: null });
	const [viewMode, setViewMode] = useState("table");
	const [attendeesState, setAttendeesState] = useState({
		isOpen: false,
		event: null,
		attendees: [],
		isLoading: false,
		error: null,
	});
	const [confirmDeleteState, setConfirmDeleteState] = useState({ isOpen: false, event: null });
	const [openMenuEventId, setOpenMenuEventId] = useState(null);
	const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, maxHeight: ACTION_MENU_PREFERRED_HEIGHT });

	const [filterCategory, setFilterCategory] = useState("");
	const [filterDate, setFilterDate] = useState("");
	const [filterTime, setFilterTime] = useState("");
	const [filterVenue, setFilterVenue] = useState("");
	const [searchQuery, setSearchQuery] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);

	const activeFilterCount = [
		Boolean(filterCategory),
		Boolean(filterDate),
		Boolean(filterTime),
		Boolean(filterVenue),
	].filter(Boolean).length;

	const isMountedRef = useRef(false);

	const toggleEventMenu = useCallback((clickEvent, eventId) => {
		clickEvent.stopPropagation();
		if (openMenuEventId === eventId) {
			setOpenMenuEventId(null);
			return;
		}
		setMenuPosition(computeFixedMenuPosition(clickEvent.currentTarget));
		setOpenMenuEventId(eventId);
	}, [openMenuEventId]);

	const fetchEvents = useCallback(async () => {
		setLoading(true);
		setErrorMessage(null);

		try {
			const res = await api.listEvents({ pageSize: 50 });
			const payload = res?.data ?? {};
			const rawItems = Array.isArray(payload.results)
				? payload.results
				: Array.isArray(payload)
				? payload
				: [];
			const metaPayload = payload.meta ?? {};
			const isSamplePayload = Boolean(metaPayload.isSample);
			const items = isSamplePayload ? [] : rawItems;

			let aggregatedAttendance = {
				scheduled: 0,
				attended: 0,
				waitlisted: 0,
			};

			const summaryByEvent = new Map();
			const detailByEvent = new Map();

			if (!isSamplePayload) {
				const detailTargets = items.filter((event) => {
					const parsedId = Number(event.id);
					return Number.isFinite(parsedId) && parsedId > 0;
				});

				if (detailTargets.length) {
					const details = await Promise.allSettled(
						detailTargets.map((event) =>
							api.getEvent(event.id).then((result) => ({
								id: event.id,
								payload: result?.data ?? {},
							})),
						),
					);

					details.forEach((detailResult) => {
						if (detailResult.status !== "fulfilled") {
							return;
						}

						const { id, payload: detailPayload } = detailResult.value ?? {};
						if (id == null) {
							return;
						}

						const detailEvent =
							detailPayload?.event && typeof detailPayload.event === "object"
								? detailPayload.event
								: detailPayload && typeof detailPayload === "object"
								? detailPayload
								: null;

						const registrations = Array.isArray(detailPayload?.registrations)
							? detailPayload.registrations
							: Array.isArray(detailPayload?.attendees)
							? detailPayload.attendees
							: [];

						if (detailEvent) {
							detailByEvent.set(id, {
								...detailEvent,
								registrations,
							});
						}

						const summary = registrations.reduce(
							(accumulator, registration) => {
								const status = registration?.status ?? "unknown";
								accumulator.total += 1;
								accumulator.statusCounts[status] =
									(accumulator.statusCounts[status] ?? 0) + 1;

								if (status === "registered") {
									accumulator.scheduled += 1;
								}
								if (status === "attended") {
									accumulator.attended += 1;
								}
								if (status === "waitlisted") {
									accumulator.waitlisted += 1;
								}

								return accumulator;
							},
							{
								total: 0,
								statusCounts: {},
								scheduled: 0,
								attended: 0,
								waitlisted: 0,
							},
						);

						summaryByEvent.set(id, summary);

						aggregatedAttendance = {
							scheduled: aggregatedAttendance.scheduled + summary.scheduled,
							attended: aggregatedAttendance.attended + summary.attended,
							waitlisted: aggregatedAttendance.waitlisted + summary.waitlisted,
						};
					});
				}
			}

			if (!isMountedRef.current) {
				return;
			}

			const enriched = items.map((event) => {
				const summary =
					summaryByEvent.get(event.id) ?? {
						total: 0,
						statusCounts: {},
						scheduled: 0,
						attended: 0,
						waitlisted: 0,
					};
				const detailEvent = detailByEvent.get(event.id) ?? {};
				const rawPreview =
					detailEvent.previewImage ?? event.previewImage ?? null;

				return {
					...event,
					...detailEvent,
					registrationSummary: summary,
					previewImage: resolvePreviewSrc(rawPreview),
					status: detailEvent.status ?? event.status ?? null,
					externalLink: detailEvent.externalLink ?? event.externalLink ?? null,
				};
			});

			const normalizedEvents = enriched;
			const resolvedMeta = {
				total: isSamplePayload
					? 0
					: Number.isFinite(Number(metaPayload.total))
					? Number(metaPayload.total)
					: normalizedEvents.length,
				page: metaPayload.page ?? 1,
				pageSize: metaPayload.pageSize ?? 50,
				isSample: isSamplePayload,
			};

			setEvents(normalizedEvents);
			setMeta(resolvedMeta);
			setAttendanceStats(aggregatedAttendance);
		} catch (err) {
			console.error("Unable to load events", err);
			if (!isMountedRef.current) {
				return;
			}
			setEvents([]);
			setAttendanceStats({ scheduled: 0, attended: 0, waitlisted: 0 });
			setMeta({ total: 0, page: 1, pageSize: 50, isSample: false });
			setErrorMessage(
				"We could not load events right now. Please try refreshing the page.",
			);
		} finally {
			if (isMountedRef.current) {
				setLoading(false);
			}
		}
	}, []);

	useEffect(() => {
		if (!openMenuEventId) return;
		const stillExists = events.some((item, index) => {
			const itemId =
				item.id ??
				item._id ??
				item.eventId ??
				item.slug ??
				item.title ??
				`event-${index}`;
			return itemId === openMenuEventId;
		});
		if (!stillExists) {
			setOpenMenuEventId(null);
		}
	}, [openMenuEventId, events]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest?.(".admin-row-actions")) {
				setOpenMenuEventId(null);
			}
		};

		const handleViewportChange = () => setOpenMenuEventId(null);

		document.addEventListener("click", handleClickOutside);
		window.addEventListener("scroll", handleViewportChange, true);
		window.addEventListener("resize", handleViewportChange, true);

		return () => {
			document.removeEventListener("click", handleClickOutside);
			window.removeEventListener("scroll", handleViewportChange, true);
			window.removeEventListener("resize", handleViewportChange, true);
		};
	}, []);

	useEffect(() => {
		isMountedRef.current = true;
		fetchEvents();
		return () => {
			isMountedRef.current = false;
		};
	}, [fetchEvents]);

	const closeAttendeesDialog = useCallback(() => {
		setAttendeesState({
			isOpen: false,
			event: null,
			attendees: [],
			isLoading: false,
			error: null,
		});
	}, []);

	useEffect(() => {
		if (!attendeesState.isOpen) {
			return;
		}

		const onEscape = (event) => {
			if (event.key === "Escape") {
				closeAttendeesDialog();
			}
		};

		window.addEventListener("keydown", onEscape);
		return () => {
			window.removeEventListener("keydown", onEscape);
		};
	}, [attendeesState.isOpen, closeAttendeesDialog]);

	const openCreateDialog = () => {
		setDialogState({ mode: "create", event: null });
		setFormState(buildFormStateFromEvent(null));
		setFormErrors({});
		setDialogError(null);
		setPreviewInputKey((previous) => previous + 1);
	};

	const openEditDialog = (event) => {
		if (!event) return;
		setDialogState({ mode: "edit", event });
		setFormState(buildFormStateFromEvent(event));
		setFormErrors({});
		setDialogError(null);
		setPreviewInputKey((previous) => previous + 1);
	};

	const closeDialog = () => {
		setDialogState({ mode: null, event: null });
		setFormState(buildFormStateFromEvent(null));
		setFormErrors({});
		setDialogError(null);
		setIsSubmitting(false);
		setIsDeleting(false);
		setPreviewInputKey((previous) => previous + 1);
		setConfirmDeleteState({ isOpen: false, event: null });
	};

	const handleFieldChange = (field) => (event) => {
		const value = event.target.value;
		setFormState((previous) => ({
			...previous,
			[field]: value,
		}));
	};

	const handlePreviewImageSelect = async (event) => {
		const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
		if (!file) {
			return;
		}

		const validationMessage = validateInitialImageFile(file, MAX_PREVIEW_FILE_SIZE);
		if (validationMessage) {
			setFormErrors((previous) => ({
				...previous,
				previewImage: validationMessage,
			}));
			setPreviewInputKey((previous) => previous + 1);
			return;
		}

		setIsImageProcessing(true);
		setDialogError(null);

		try {
			const compressedImage = await compressImageForUpload(file);
		setFormState((previous) => ({
			...previous,
			previewImageFile: compressedImage,
		}));
		setFormErrors((previous) => {
			if (!previous.previewImage) {
				return previous;
			}
			const { previewImage, ...rest } = previous;
			return rest;
		});
		} catch (error) {
			setFormErrors((previous) => ({
				...previous,
				previewImage: error?.message || "Unable to compress image. Please try another file.",
			}));
			setPreviewInputKey((previous) => previous + 1);
		} finally {
			setIsImageProcessing(false);
		}
	};

	const handleClearPreviewImage = () => {
		setFormState((previous) => ({
			...previous,
			previewImageFile: null,
		}));
		setFormErrors((previous) => {
			if (!previous.previewImage) {
				return previous;
			}
			const { previewImage, ...rest } = previous;
			return rest;
		});
		setPreviewInputKey((previous) => previous + 1);
	};

	const handleRemoveExistingPreview = () => {
		setFormState((previous) => ({
			...previous,
			previewImageFile: null,
			previewImageUrl: "",
		}));
		setFormErrors((previous) => {
			if (!previous.previewImage) {
				return previous;
			}
			const { previewImage, ...rest } = previous;
			return rest;
		});
		setPreviewInputKey((previous) => previous + 1);
	};

	const openDeleteConfirm = () => {
		if (dialogState.mode !== "edit" || !dialogState.event) {
			return;
		}
		setDialogError(null);
		setConfirmDeleteState({ isOpen: true, event: dialogState.event });
	};

	const closeDeleteConfirm = () => {
		if (isDeleting) {
			return;
		}
		setConfirmDeleteState({ isOpen: false, event: null });
	};

	const openEventPreview = (event) => {
		if (!event) return;
		setPreviewState({ isOpen: true, event });
	};

	const closeEventPreview = () => {
		setPreviewState({ isOpen: false, event: null });
	};

	const openAttendeesDialog = useCallback(async (event) => {
		if (!event) {
			return;
		}

		setAttendeesState({
			isOpen: true,
			event,
			attendees: normalizeAttendees(event.registrations),
			isLoading: true,
			error: null,
		});

		try {
			const eventId = Number(event.id ?? event.eventId ?? event._id);
			if (!Number.isFinite(eventId) || eventId <= 0) {
				setAttendeesState((previous) => ({
					...previous,
					isLoading: false,
				}));
				return;
			}

			const detailResponse = await api.getEvent(eventId);
			const detailPayload = detailResponse?.data ?? {};
			const rawRegistrations = Array.isArray(detailPayload?.registrations)
				? detailPayload.registrations
				: [];

			setAttendeesState((previous) => ({
				...previous,
				event: detailPayload?.event ? { ...event, ...detailPayload.event } : event,
				attendees: normalizeAttendees(rawRegistrations),
				isLoading: false,
				error: null,
			}));
		} catch (error) {
			console.error("Unable to load attendees", error);
			setAttendeesState((previous) => ({
				...previous,
				isLoading: false,
				error: "Unable to load attendees right now.",
			}));
		}
	}, []);

	const handleExternalToggle = (event) => {
		const checked = event.target.checked;
		setFormState((previous) => ({
			...previous,
			externalLinkEnabled: checked,
			externalLink: checked ? previous.externalLink : "",
		}));
		setFormErrors((previous) => {
			if (!checked && previous.externalLink) {
				const { externalLink, ...rest } = previous;
				return rest;
			}
			return previous;
		});
	};

	const enableExternalLink = () => {
		setFormState((previous) => {
			if (previous.externalLinkEnabled) {
				return previous;
			}
			return {
				...previous,
				externalLinkEnabled: true,
			};
		});
	};

	const validateForm = () => {
		const errors = {};

		if (!formState.title.trim()) {
			errors.title = "Event title is required.";
		}
		if (!formState.category) {
			errors.category = "Select a category.";
		}
		if (!formState.description.trim()) {
			errors.description = "Provide an event description.";
		}
		if (!formState.date) {
			errors.date = "Event date is required.";
		}
		if (!formState.time) {
			errors.time = "Event time is required.";
		}
		if (!formState.venue.trim()) {
			errors.venue = "Venue or location is required.";
		}
		if (!formState.status) {
			errors.status = "Select a status.";
		}

		const capacityValue = formState.capacity.trim();
		if (capacityValue && Number.isNaN(Number(capacityValue))) {
			errors.capacity = "Enter a valid capacity number.";
		}

		if (formState.externalLinkEnabled) {
			const linkValue = formState.externalLink.trim();
			if (!linkValue) {
				errors.externalLink = "Provide an external link URL.";
			}
		}

		const selectedPreview = formState.previewImageFile;
		if (dialogState.mode === "create" && !selectedPreview) {
			errors.previewImage = "Event thumbnail image is required.";
		}
		if (selectedPreview && !errors.previewImage) {
			const validationMessage = validateInitialImageFile(selectedPreview, MAX_PREVIEW_FILE_SIZE);
			if (validationMessage) {
				errors.previewImage = validationMessage;
			} else if (selectedPreview.size > MAX_COMPRESSED_PREVIEW_FILE_SIZE) {
				errors.previewImage = "Image must be compressed to 1MB or smaller before upload.";
			}
		}

		if (isImageProcessing) {
			errors.previewImage = "Image is still compressing. Please wait.";
		}

		return errors;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (isImageProcessing) {
			setDialogError("Image is still compressing. Please wait before saving.");
			return;
		}

		const errors = validateForm();
		setFormErrors(errors);
		if (Object.keys(errors).length) {
			return;
		}

		const capacityValue = formState.capacity.trim();
		const startDateTime = combineDateTime(formState.date, formState.time);
		const existingCreator = dialogState.event?.createdBy;
		const parsedCreator = existingCreator !== undefined && existingCreator !== null
			? Number(existingCreator)
			: NaN;
		const resolvedAdminId = Number.isFinite(parsedCreator) && parsedCreator > 0
			? parsedCreator
			: DEFAULT_ADMIN_ID;

		const formData = new FormData();
		formData.append("title", formState.title.trim());
		formData.append("category", formState.category);
		formData.append("description", formState.description.trim());
		formData.append("venue", formState.venue.trim());
		formData.append("startAt", startDateTime);
		formData.append("endAt", startDateTime);
		formData.append("status", formState.status);
		if (capacityValue) {
			formData.append("capacity", capacityValue);
		} else if (dialogState.mode === "edit") {
			formData.append("capacity", "");
		}

		if (formState.previewImageFile) {
			formData.append("previewImageFile", formState.previewImageFile);
		} else if (dialogState.mode === "edit") {
			if (!formState.previewImageUrl) {
				// Explicitly clear the stored preview if the existing one has been removed
				formData.append("previewImage", "");
			} else {
				// Keep the current preview when editing without uploading a new file
				formData.append("previewImage", formState.previewImageUrl.trim());
			}
		}

		if (formState.externalLinkEnabled) {
			formData.append("externalLink", formState.externalLink.trim());
		} else if (dialogState.mode === "edit") {
			formData.append("externalLink", "");
		}

		formData.append("adminId", String(resolvedAdminId));

		setIsSubmitting(true);
		setDialogError(null);

		try {
			if (dialogState.mode === "edit" && dialogState.event?.id) {
				await api.updateEvent(dialogState.event.id, formData);
			} else {
				await api.createEvent(formData);
			}
			await fetchEvents();
			closeDialog();
		} catch (err) {
			console.error("Unable to save event", err);
			if (isMountedRef.current) {
				const serverPayload = err?.response?.data ?? null;
				const fallback = "Unable to save event. Please try again.";
				const composed = buildServerErrorMessage(serverPayload, fallback);
				setDialogError(composed);
			}
		} finally {
			if (isMountedRef.current) {
				setIsSubmitting(false);
			}
		}
	};

	const handleDelete = async () => {
		if (!confirmDeleteState.isOpen || !confirmDeleteState.event?.id) {
			return;
		}

		const parsedId = Number(confirmDeleteState.event.id);
		if (!Number.isFinite(parsedId) || parsedId <= 0) {
			return;
		}

		setIsDeleting(true);
		setDialogError(null);
		setConfirmDeleteState((previous) => ({ ...previous, isOpen: true }));

		try {
			await api.deleteEvent(parsedId);
			await fetchEvents();
			closeDialog();
		} catch (err) {
			console.error("Unable to delete event", err);
			if (isMountedRef.current) {
				const serverPayload = err?.response?.data ?? null;
				const fallback = "Unable to delete event. Please try again.";
				const composed = buildServerErrorMessage(serverPayload, fallback);
				setDialogError(composed);
				setConfirmDeleteState((previous) => ({ ...previous, isOpen: true }));
			}
		} finally {
			if (isMountedRef.current) {
				setIsDeleting(false);
			}
		}
	};

	const renderStatusChip = (event) => {
		if (event.status) {
			return (
				<span className={`admin-chip ${getStatusTone(event.status)}`}>
					{event.status}
				</span>
			);
		}

		const startDate = event.startAt ? new Date(event.startAt) : null;
		const endDate = event.endAt ? new Date(event.endAt) : null;
		const now = new Date();

		if (startDate && endDate && now >= startDate && now <= endDate) {
			return <span className="admin-chip is-orange">In progress</span>;
		}

		if (startDate && now >= startDate) {
			return <span className="admin-chip is-green">Completed</span>;
		}

		return <span className="admin-chip is-blue">Upcoming</span>;
	};

	// Derive unique venue list from events for the venue filter dropdown
	const venueOptions = [...new Set(
		events
			.map((e) => e.venue?.trim())
			.filter(Boolean),
	)].sort((a, b) => a.localeCompare(b));

	// Derive unique time-of-day options from events
	const timeOptions = [...new Set(
		events
			.map((e) => {
				if (!e.startAt) return null;
				const d = new Date(e.startAt);
				if (Number.isNaN(d.getTime())) return null;
				const h = d.getHours();
				if (h < 12) return "morning";
				if (h < 17) return "afternoon";
				return "evening";
			})
			.filter(Boolean),
	)].sort();

	const TIME_LABELS = { morning: "Morning (before 12 PM)", afternoon: "Afternoon (12–5 PM)", evening: "Evening (after 5 PM)" };

	// Apply client-side filters
	const filteredEvents = useMemo(() => events.filter((eventItem) => {
		if (filterCategory && eventItem.category !== filterCategory) return false;

		if (filterDate) {
			const eventDate = eventItem.startAt ? toInputDate(eventItem.startAt) : "";
			if (eventDate !== filterDate) return false;
		}

		if (filterTime && eventItem.startAt) {
			const d = new Date(eventItem.startAt);
			if (!Number.isNaN(d.getTime())) {
				const h = d.getHours();
				const slot = h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
				if (slot !== filterTime) return false;
			}
		} else if (filterTime && !eventItem.startAt) {
			return false;
		}

		if (filterVenue) {
			const v = eventItem.venue?.trim() ?? "";
			if (v !== filterVenue) return false;
		}

		const normalizedQuery = searchQuery.trim().toLowerCase();
		if (normalizedQuery) {
			const categoryLabel = CATEGORY_LABEL_LOOKUP.get(eventItem.category) ?? eventItem.category ?? "";
			const searchableValues = [
				eventItem?.title,
				eventItem?.venue,
				eventItem?.status,
				categoryLabel,
				eventItem?.description,
			];

			const matchesQuery = searchableValues.some((value) =>
				String(value ?? "").toLowerCase().includes(normalizedQuery),
			);

			if (!matchesQuery) return false;
		}

		return true;
	}), [events, filterCategory, filterDate, filterTime, filterVenue, searchQuery]);

	const totalPages = Math.max(1, Math.ceil(filteredEvents.length / TABLE_PAGE_SIZE));

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	const paginatedEvents = useMemo(() => {
		const startIndex = (currentPage - 1) * TABLE_PAGE_SIZE;
		return filteredEvents.slice(startIndex, startIndex + TABLE_PAGE_SIZE);
	}, [currentPage, filteredEvents]);

	const handleSearchChange = (event) => {
		setSearchQuery(event.target.value);
		setCurrentPage(1);
	};

	const handlePreviousPage = () => {
		setCurrentPage((previous) => Math.max(1, previous - 1));
	};

	const handleNextPage = () => {
		setCurrentPage((previous) => Math.min(totalPages, previous + 1));
	};

	const hasActiveFilters = filterCategory || filterDate || filterTime || filterVenue || searchQuery.trim();

	const clearAllFilters = () => {
		setFilterCategory("");
		setFilterDate("");
		setFilterTime("");
		setFilterVenue("");
		setSearchQuery("");
		setCurrentPage(1);
	};

	return (
		<div className="admin-page admin-stack-lg event-management-page">
			<header className="admin-surface ev-hero">
				<div className="ev-hero__intro">
					<span className="ev-hero__eyebrow">Events Console</span>
					<h1>Event Management</h1>
					<p className="admin-muted">
						Coordinate union events and attendance tracking.
					</p>
				</div>
				<div className="ev-header-actions ev-hero__actions">
					<button
						type="button"
						className={`admin-icon-button admin-icon-button--refresh ${loading ? "is-loading" : ""}`}
						onClick={fetchEvents}
						disabled={loading}
						aria-label="Refresh events"
						title="Refresh events"
					>
						<RefreshCw size={18} />
					</button>
					{/* <span className="admin-pill">Event calendar</span> */}
					<button
						type="button"
						className="admin-button is-primary"
						onClick={openCreateDialog}
					>
						<Plus size={16} />
						Create event
					</button>
				</div>
			</header>

			<section className="admin-card-grid cols-3 ev-overview">
				<article className="admin-card">
					<div className="admin-card__label">Events total</div>
					<div className="admin-row">
						<div className="admin-card__value">{loading ? "—" : meta.total}</div>
						<span className="admin-chip is-blue"><CalendarDays size={18} /></span>
					</div>
					<div className="admin-card__meta">Upcoming and past events</div>
				</article>
				<article className="admin-card">
					<div className="admin-card__label">Scheduled attendees</div>
					<div className="admin-row">
						<div className="admin-card__value">
							{loading ? "—" : attendanceStats.scheduled}
						</div>
						<span className="admin-chip is-orange"><Users size={18} /></span>
					</div>
					<div className="admin-card__meta">
						Active registrations across events
					</div>
				</article>
				<article className="admin-card">
					<div className="admin-card__label">Attended / waitlisted</div>
					<div className="admin-row">
						<div className="admin-card__value">
							{loading
								? "—"
								: `${attendanceStats.attended} / ${attendanceStats.waitlisted}`}
						</div>
						<span className="admin-chip is-green"><CheckCircle2 size={18} /></span>
					</div>
					<div className="admin-card__meta">
						Historical attendance vs waitlist
					</div>
				</article>
			</section>

			{/* Filter bar */}
				{!loading && events.length > 0 && (
					<div className="admin-surface ev-toolbar ev-toolbar-surface">
						<div className="admin-row members-table__search-row ev-search-row">
							<div className="ev-search-field">
								<span className="admin-card__label">Search Events</span>
								<div className="admin-card ev-search-card">
									<Search size={18} color="#64748b" />
									<input
										type="text"
										value={searchQuery}
										onChange={handleSearchChange}
										placeholder="Search title, venue, category, or status"
										className="ev-search-input"
									/>
								</div>
							</div>
							<div className="id-card-queue__filter-button-wrap ev-filter-toggle-wrap">
								<div className="admin-view-toggle">
									<button
										type="button"
										className={`admin-view-toggle__btn ${viewMode === "table" ? "is-active" : ""}`}
										onClick={() => setViewMode("table")}
										aria-label="Table View"
										title="Table View"
									>
										<List size={18} />
									</button>
									<button
										type="button"
										className={`admin-view-toggle__btn ${viewMode === "card" ? "is-active" : ""}`}
										onClick={() => setViewMode("card")}
										aria-label="Card View"
										title="Card View"
									>
										<Grid size={18} />
									</button>
								</div>
								<button
									type="button"
									className={`admin-button admin-button--ghost ev-filter-toggle ${showFilters ? "is-open" : ""} ${activeFilterCount > 0 ? "is-active" : ""}`}
									onClick={() => setShowFilters((open) => !open)}
									aria-pressed={showFilters}
								>
									<Filter size={18} /> Filters
									{activeFilterCount > 0 ? (
										<span className="ev-filter-toggle__badge">{activeFilterCount}</span>
									) : null}
								</button>
							</div>
						</div>

						{showFilters ? (
							<div className="ev-filters">
								<div className="ev-filters__field">
									<label>Category</label>
									<select
										value={filterCategory}
										onChange={(e) => setFilterCategory(e.target.value)}
									>
										<option value="">All Categories</option>
										{CATEGORY_OPTIONS.map((opt) => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</select>
								</div>
								<div className="ev-filters__field">
									<label>Date</label>
									<input
										type="date"
										value={filterDate}
										onChange={(e) => setFilterDate(e.target.value)}
									/>
								</div>
								<div className="ev-filters__field">
									<label>Time of Day</label>
									<select
										value={filterTime}
										onChange={(e) => setFilterTime(e.target.value)}
									>
										<option value="">All Times</option>
										{timeOptions.map((slot) => (
											<option key={slot} value={slot}>{TIME_LABELS[slot] ?? slot}</option>
										))}
									</select>
								</div>
								<div className="ev-filters__field">
									<label>Venue</label>
									<select
										value={filterVenue}
										onChange={(e) => setFilterVenue(e.target.value)}
									>
										<option value="">All Venues</option>
										{venueOptions.map((v) => (
											<option key={v} value={v}>{v}</option>
										))}
									</select>
								</div>
								{hasActiveFilters && (
									<div className="ev-filters__field ev-filters__field--action">
										<label>&nbsp;</label>
										<button
											type="button"
											className="ev-filters__clear"
											onClick={clearAllFilters}
										>
											<RotateCcw size={14} />
											Clear Filters
										</button>
									</div>
								)}
								{hasActiveFilters && (
									<div className="ev-filters__result-count">
										Showing {filteredEvents.length} of {events.length} event{events.length !== 1 ? "s" : ""}
									</div>
								)}
							</div>
						) : null}
					</div>
				)}

			<section className="admin-surface admin-stack-md ev-table-surface">
				{errorMessage && !loading ? (
					<div className="admin-empty-state">
						<p>{errorMessage}</p>
					</div>
				) : (
					<div className="admin-stack">
						{loading ? (
							<div className="admin-empty-state">
								<p>Loading…</p>
							</div>
						) : events.length === 0 ? (
							<div className="admin-empty-state">
								<p>
									No events have been scheduled yet. Add an event to see it listed here.
								</p>
							</div>
						) : filteredEvents.length === 0 ? (
							<div className="admin-empty-state">
								<p>
									No events match the selected filters. Try adjusting your criteria.
								</p>
							</div>
						) : (
							<>
							<div className={`admin-view-transition ${viewMode === "card" ? "is-card-view" : "is-table-view"}`}>
								{viewMode === "card" ? (
									<div className="admin-card-grid ev-card-grid">
										{paginatedEvents.map((eventItem, index) => {
											const resolvedEventId =
												eventItem.id ??
												eventItem._id ??
												eventItem.eventId ??
												eventItem.slug ??
												eventItem.title ??
												`event-${index}`;
											const categoryLabel =
												CATEGORY_LABEL_LOOKUP.get(eventItem.category) ??
												eventItem.category ??
												"Other";

											const hasExternalLink = Boolean(eventItem.externalLink);
											const dateLabel = formatDateLabel(eventItem.startAt);
											const timeLabel = formatTimeLabel(eventItem.startAt);
											const descriptionPreview = buildDescriptionPreview(
												eventItem.description,
												90,
											);
											const titleLabel = eventItem.title?.trim() || "Untitled event";
											const venueLabel = eventItem.venue?.trim() || "—";
											const numericIdentifier = Number(
												eventItem.id ?? eventItem.eventId ?? eventItem._id,
											);
											const canEdit = Number.isFinite(numericIdentifier) && numericIdentifier > 0;
											const scheduledCount = Number(eventItem?.registrationSummary?.scheduled ?? 0);
											const attendedCount = Number(eventItem?.registrationSummary?.attended ?? 0);
											const attendeeCount = scheduledCount + attendedCount;
											const capacityValue = Number(eventItem?.capacity);
											const hasCapacity = Number.isFinite(capacityValue) && capacityValue > 0;
											
											return (
												<div className="admin-card ev-event-card" key={resolvedEventId}>
													<div className="ev-event-card__image-wrap">
														{eventItem.previewImage ? (
															<img
																src={eventItem.previewImage}
																alt={`Preview for ${titleLabel}`}
																loading="lazy"
																className="ev-event-card__image"
															/>
														) : (
															<div className="admin-image-fallback ev-event-card__image-fallback">
																<span>No preview</span>
															</div>
														)}
														<div className="ev-event-card__badges">
															<span className="ev-badge">{categoryLabel}</span>
														</div>
													</div>
													<div className="ev-event-card__content">
														<h4 className="ev-event-card__title" title={titleLabel}>{titleLabel}</h4>
														<div className="ev-event-card__meta-inline">
															<span title={venueLabel}>
																<MapPin size={12} /> {venueLabel}
															</span>
															<span>
																<Clock size={12} /> {dateLabel} {timeLabel}
															</span>
															<span>
																<Users size={12} /> {hasCapacity ? `${attendeeCount} / ${capacityValue}` : attendeeCount}
															</span>
														</div>
														<p className="ev-event-card__desc">{descriptionPreview}</p>
													</div>
													<div className="ev-event-card__footer">
														{hasExternalLink ? (
															<button
																type="button"
																className="admin-button admin-button--secondary ev-event-card__btn-link"
																onClick={() => window.open(eventItem.externalLink, "_blank", "noopener,noreferrer")}
															>
																<ExternalLink size={14} /> Link
															</button>
														) : (
															<span className="ev-event-card__no-link admin-muted">No external link</span>
														)}
														<div className="ev-event-card__actions">
															<button
																type="button"
																className="admin-icon-button"
																onClick={() => {
																	if (canEdit) openEditDialog(eventItem);
																}}
																disabled={!canEdit}
																title="Edit"
															>
																<PencilLine size={16} />
															</button>
															<div className="admin-row-actions">
																<button
																	type="button"
																	className="admin-icon-button"
																	onClick={(clickEvent) => toggleEventMenu(clickEvent, resolvedEventId)}
																	aria-haspopup="menu"
																	aria-expanded={openMenuEventId === resolvedEventId}
																	title="More actions"
																>
																	<MoreHorizontal size={16} />
																</button>
																{openMenuEventId === resolvedEventId ? (
																	<div
																		className="admin-menu admin-menu--fixed"
																		style={{
																			"--admin-menu-top": `${menuPosition.top}px`,
																			"--admin-menu-left": `${menuPosition.left}px`,
																			"--admin-menu-max-height": `${menuPosition.maxHeight}px`,
																		}}
																	>
																		<button
																			type="button"
																			onClick={(menuEvent) => {
																				menuEvent.stopPropagation();
																				setOpenMenuEventId(null);
																				openAttendeesDialog(eventItem);
																			}}
																		>
																			<Users size={14} />
																			View attendees
																		</button>
																		<button
																			type="button"
																			onClick={(menuEvent) => {
																				menuEvent.stopPropagation();
																				setOpenMenuEventId(null);
																				openEventPreview(eventItem);
																			}}
																		>
																			<ExternalLink size={14} />
																			Preview
																		</button>
																	</div>
																) : null}
															</div>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								) : (
									<div className="admin-events-table-wrapper admin-table-wrapper admin-scroll">
										<table className="admin-table admin-events-table">
											<colgroup>
												{EVENT_TABLE_COLUMN_WIDTHS.map((width, index) => (
													<col key={`events-col-${index}`} style={{ width }} />
												))}
											</colgroup>
											<thead>
												<tr>
													<th scope="col">Event Preview</th>
													<th scope="col">Details</th>
													<th scope="col">Category</th>
													<th scope="col">Date &amp; Time</th>
													<th scope="col">Attendees</th>
													<th scope="col">External Link</th>
													<th scope="col">Status</th>
													<th scope="col" className="admin-events-actions-header">Actions</th>
												</tr>
											</thead>
											<tbody>
												{paginatedEvents.map((eventItem, index) => {
													const resolvedEventId =
														eventItem.id ??
														eventItem._id ??
														eventItem.eventId ??
														eventItem.slug ??
														eventItem.title ??
														`event-${index}`;
													const categoryLabel =
														CATEGORY_LABEL_LOOKUP.get(eventItem.category) ??
														eventItem.category ??
														"Other";

													const hasExternalLink = Boolean(eventItem.externalLink);
													const dateLabel = formatDateLabel(eventItem.startAt);
													const timeLabel = formatTimeLabel(eventItem.startAt);
													const createdValue =
														eventItem.createdAt ??
														eventItem.created_at ??
														eventItem.created ??
														eventItem.createdOn ??
														eventItem.createdDate ??
														null;
													const createdLabel = formatDateLabel(createdValue);
													const descriptionPreview = buildDescriptionPreview(
														eventItem.description,
														90,
													);
													const titleLabel = eventItem.title?.trim() || "Untitled event";
													const venueLabel = eventItem.venue?.trim() || "—";
													const numericIdentifier = Number(
														eventItem.id ?? eventItem.eventId ?? eventItem._id,
													);
													const canEdit = Number.isFinite(numericIdentifier) && numericIdentifier > 0;
													const rowIsInteractive = hasExternalLink;
															const scheduledCount = Number(eventItem?.registrationSummary?.scheduled ?? 0);
															const attendedCount = Number(eventItem?.registrationSummary?.attended ?? 0);
															const attendeeCount = scheduledCount + attendedCount;
															const capacityValue = Number(eventItem?.capacity);
															const hasCapacity = Number.isFinite(capacityValue) && capacityValue > 0;

													return (
														<tr
															key={resolvedEventId}
															onClick={() => {
																if (!rowIsInteractive) return;
																window.open(eventItem.externalLink, "_blank", "noopener,noreferrer");
															}}
															onKeyDown={(keyboardEvent) => {
																if (!rowIsInteractive) return;
																if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
																	keyboardEvent.preventDefault();
																	window.open(eventItem.externalLink, "_blank", "noopener,noreferrer");
																}
															}}
															role={rowIsInteractive ? "link" : undefined}
															tabIndex={rowIsInteractive ? 0 : -1}
															style={{ cursor: rowIsInteractive ? "pointer" : "default" }}
															className="admin-table-row-enhanced"
														>
															<td>
																<div className="admin-events-preview">
																	<div className="admin-events-preview__image ev-thumbnail-lg">
																		{eventItem.previewImage ? (
																			<img
																				src={eventItem.previewImage}
																				alt={`Preview for ${eventItem.title}`}
																				loading="lazy"
																			/>
																		) : (
																			<div className="admin-image-fallback">
																				<span>No preview</span>
																			</div>
																		)}
																	</div>
																	<div className="admin-events-preview__text">
																		<span
																			className="admin-events-preview__title admin-text-strong"
																			title={titleLabel}
																		>
																			{titleLabel}
																		</span>
																		<span
																			className="admin-events-preview__description admin-text-muted"
																			title={eventItem.description?.trim() || "No description provided yet."}
																		>
																			{descriptionPreview}
																		</span>
																	</div>
																</div>
															</td>
															<td>
																<div className="admin-events-details">
																	<div className="admin-events-details__item" title={venueLabel}>
																		<MapPin size={14} />
																		<span>{venueLabel}</span>
																	</div>
																	<div className="admin-events-details__item">
																		<CalendarDays size={14} />
																		<span>Created: {createdLabel}</span>
																	</div>
																</div>
															</td>
															<td>
																<span className="admin-pill is-soft">{categoryLabel}</span>
															</td>
															<td>
																<div className="admin-events-schedule">
																	<span>{dateLabel}</span>
																	<span>{timeLabel}</span>
																</div>
															</td>
															<td>
																<div className="admin-events-schedule">
																	<span>{hasCapacity ? `${attendeeCount} / ${capacityValue}` : attendeeCount}</span>
																	<span className="admin-text-muted">{hasCapacity ? "registered+attended" : "attending"}</span>
																</div>
															</td>
															<td>
																{hasExternalLink ? (
																	<a
																		href={eventItem.externalLink}
																		target="_blank"
																		rel="noopener noreferrer"
																		className="admin-link-quiet"
																	>
																		Yes
																	</a>
																) : (
																	<span className="admin-text-muted">No</span>
																)}
															</td>
															<td className="admin-events-status-cell">{renderStatusChip(eventItem)}</td>
															<td>
																<div className="admin-row-actions">
																	<button
																		type="button"
																		className="admin-icon-button"
																		onClick={(clickEvent) => toggleEventMenu(clickEvent, resolvedEventId)}
																		aria-haspopup="menu"
																		aria-expanded={openMenuEventId === resolvedEventId}
																		title="More actions"
																	>
																		<MoreHorizontal size={16} />
																	</button>
																	{openMenuEventId === resolvedEventId ? (
																		<div
																			className="admin-menu admin-menu--fixed"
																			style={{
																				"--admin-menu-top": `${menuPosition.top}px`,
																				"--admin-menu-left": `${menuPosition.left}px`,
																				"--admin-menu-max-height": `${menuPosition.maxHeight}px`,
																			}}
																		>
																			<button
																				type="button"
																				onClick={(menuEvent) => {
																					menuEvent.stopPropagation();
																					if (!canEdit) return;
																					setOpenMenuEventId(null);
																					openEditDialog(eventItem);
																				}}
																				disabled={!canEdit}
																			>
																				<PencilLine size={14} />
																				Edit
																			</button>
																			<button
																				type="button"
																				onClick={(menuEvent) => {
																					menuEvent.stopPropagation();
																					setOpenMenuEventId(null);
																					openAttendeesDialog(eventItem);
																				}}
																			>
																				<Users size={14} />
																				View attendees
																			</button>
																			<button
																				type="button"
																				onClick={(menuEvent) => {
																					menuEvent.stopPropagation();
																					setOpenMenuEventId(null);
																					openEventPreview(eventItem);
																				}}
																			>
																				<ExternalLink size={14} />
																				Preview
																			</button>
																		</div>
																	) : null}
																</div>
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								)}
							</div>
							<footer className="admin-row ev-table__footer">
								<span className="admin-muted">Page {currentPage} of {totalPages}</span>
								<span className="admin-pill is-blue">Showing {paginatedEvents.length} of {filteredEvents.length}</span>
								<div className="admin-actions">
									<button
										type="button"
										className="admin-button ev-pager-button"
										onClick={handlePreviousPage}
										disabled={currentPage <= 1}
									>
										<ChevronLeft size={16} /> Previous
									</button>
									<button
										type="button"
										className="admin-button ev-pager-button"
										onClick={handleNextPage}
										disabled={currentPage >= totalPages}
									>
										Next <ChevronRight size={16} />
									</button>
								</div>
							</footer>
							</>
						)}
					</div>
				)}
			</section>

			{previewState.isOpen && previewState.event ? (
							<div className="admin-dialog" role="dialog" aria-modal="true">
								<div className="admin-dialog__backdrop" onClick={closeEventPreview} role="presentation" />
								<div className="admin-dialog__panel ev-preview-panel">
									<div className="admin-dialog__header">
										<div>
											<h3>Event Preview</h3>
											<p className="admin-muted">
												Review how this event will appear to members.
											</p>
										</div>
										<button
											type="button"
											className="admin-dialog__close"
											onClick={closeEventPreview}
											aria-label="Close event preview"
										>
											<X size={18} />
										</button>
									</div>
										<div className="admin-dialog__body ev-preview-layout">
										<div className="ev-preview-card">
											{previewState.event.previewImage ? (
												<div className="ev-preview-card__image-wrap">
													<img
															src={previewState.event.previewImage}
															alt={`Preview for ${previewState.event.title ?? "event"}`}
															loading="lazy"
															className="ev-preview-card__image"
													/>
												</div>
											) : (
												<div className="ev-preview-card__no-image">
													<span className="admin-muted">No preview image</span>
												</div>
											)}
										</div>
										<div className="ev-preview-info">
											<div className="ev-preview-badges">
												<span className="admin-chip is-blue">
													{CATEGORY_LABEL_LOOKUP.get(previewState.event.category) ?? previewState.event.category ?? "Other"}
												</span>
												<span className={`admin-chip ${getStatusTone(previewState.event.status)}`}>
													{previewState.event.status ?? "Draft"}
												</span>
											</div>
											<h4>{previewState.event.title || "Untitled event"}</h4>
											<p className="admin-muted">
												{buildDescriptionPreview(previewState.event.description, 220)}
											</p>
										</div>
										<div className="ev-preview-meta-grid">
											<div className="ev-preview-meta-item">
												<span className="ev-preview-meta-label">Date &amp; Time</span>
												<div className="ev-preview-meta-value">
													<CalendarDays size={14} />
													<span>{`${formatDateLabel(previewState.event.startAt)} · ${formatTimeLabel(previewState.event.startAt)}`}</span>
												</div>
											</div>
											<div className="ev-preview-meta-item">
												<span className="ev-preview-meta-label">Venue</span>
												<div className="ev-preview-meta-value">
													<MapPin size={14} />
													<span>{previewState.event.venue || "Venue to be announced"}</span>
												</div>
											</div>
											<div className="ev-preview-meta-item">
												<span className="ev-preview-meta-label">Registration</span>
												<div className="ev-preview-meta-value">
													<Clock size={14} />
													<span>{previewState.event.externalLink ? "External link enabled" : "In-app registration"}</span>
												</div>
											</div>
										</div>
										<div className="ev-preview-action-surface">
											{previewState.event.externalLink ? (
												<a
													href={previewState.event.externalLink}
													target="_blank"
													rel="noopener noreferrer"
													className="admin-button is-primary ev-preview-link"
												>
													<ExternalLink size={14} />
													Visit external link
												</a>
											) : (
												<p className="admin-muted ev-no-external">
													No external link provided for this event.
												</p>
											)}
										</div>
									</div>
								</div>
							</div>
						) : null}

			{attendeesState.isOpen ? (
				<div className="admin-dialog" role="dialog" aria-modal="true">
					<div className="admin-dialog__backdrop" onClick={closeAttendeesDialog} role="presentation" />
					<div className="admin-dialog__panel ev-attendees-panel">
						<div className="admin-dialog__header">
							<div>
								<h3>Event Attendees</h3>
								<p className="admin-muted">
									{attendeesState.event?.title || "Untitled event"}
									{" · "}
									{attendeesState.attendees.length} attending
								</p>
							</div>
							<button
								type="button"
								className="admin-dialog__close"
								onClick={closeAttendeesDialog}
								aria-label="Close attendees"
							>
								<X size={18} />
							</button>
						</div>
						<div className="admin-dialog__body">
							{attendeesState.isLoading ? (
								<div className="admin-empty-state">
									<p>Loading attendees...</p>
								</div>
							) : attendeesState.error ? (
								<div className="ev-dialog-error">{attendeesState.error}</div>
							) : attendeesState.attendees.length === 0 ? (
								<div className="admin-empty-state">
									<p>No attendees yet for this event.</p>
								</div>
							) : (
								<div className="ev-attendees-table-wrapper admin-scroll">
									<table className="admin-table ev-attendees-table">
										<thead>
											<tr>
												<th scope="col">Name</th>
												<th scope="col">Membership No</th>
												<th scope="col">Status</th>
												<th scope="col">Registered At</th>
											</tr>
										</thead>
										<tbody>
											{attendeesState.attendees.map((attendee, index) => (
												<tr key={`${attendee.id ?? attendee.membershipNo ?? attendee.userId ?? "attendee"}-${index}`}>
													<td>{attendee.name}</td>
													<td>{attendee.membershipNo ?? "—"}</td>
													<td>{attendee.status}</td>
													<td>{formatDateTime(attendee.registeredAt)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
						<div className="admin-dialog__footer">
							<button
								type="button"
								className="admin-button admin-button--ghost"
								onClick={closeAttendeesDialog}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			) : null}

			{dialogState.mode ? (
				<div className="admin-dialog" role="dialog" aria-modal="true">
					<div
						className="admin-dialog__backdrop"
						role="presentation"
						onClick={closeDialog}
					/>
					<div className="admin-dialog__panel ev-dialog-panel">
						<div className="admin-dialog__header">
							<div>
								<h3>
									{dialogState.mode === "create"
										? "Create New Event"
										: "Edit Event"}
								</h3>
								<p className="admin-muted">
									{dialogState.mode === "create"
										? "Provide event information that will appear in the mobile app."
										: "Update the event details and save your changes."}
								</p>
							</div>
							<button
								type="button"
								className="admin-dialog__close"
								onClick={closeDialog}
								aria-label="Close event modal"
							>
								<X size={18} />
							</button>
						</div>
						<form onSubmit={handleSubmit} className="admin-stack-md">
							<div className="admin-dialog__body">
								{dialogError ? (
									<div className="ev-dialog-error">
										{dialogError}
									</div>
								) : null}

								<div className="event-dialog">
									<section className="event-dialog__section">
										<header className="event-dialog__section-header">
											<p className="event-dialog__section-eyebrow">Event basics</p>
											<p className="event-dialog__section-subtitle">
												These details appear on the Events directory cards.
											</p>
										</header>
										<div className="event-dialog__grid event-dialog__grid--two">
											<div className="admin-field">
												<span>Event Title *</span>
												<input
													type="text"
													placeholder="Enter event title"
													value={formState.title}
													onChange={handleFieldChange("title")}
												/>
												{formErrors.title ? (
													<small className="ev-field-error">{formErrors.title}</small>
												) : null}
											</div>
											<div className="admin-field">
												<span>Category *</span>
												<select
													value={formState.category}
													onChange={handleFieldChange("category")}
												>
													{CATEGORY_OPTIONS.map((option) => (
														<option key={option.value} value={option.value}>
															{option.label}
														</option>
													))}
												</select>
												{formErrors.category ? (
													<small className="ev-field-error">{formErrors.category}</small>
												) : null}
											</div>
										</div>
										<div className="admin-field event-dialog__field-full">
											<span>Event Description *</span>
											<textarea
												rows={4}
												placeholder="Provide a detailed description that will appear in the mobile app"
												value={formState.description}
												onChange={handleFieldChange("description")}
											/>
											{formErrors.description ? (
												<small className="ev-field-error">
													{formErrors.description}
												</small>
											) : (
												<small>
													Include details such as agenda, key speakers, or registration notes.
												</small>
											)}
										</div>
									</section>

									<section className="event-dialog__section">
										<header className="event-dialog__section-header">
											<p className="event-dialog__section-eyebrow">Schedule &amp; status</p>
											<p className="event-dialog__section-subtitle">
												Confirm when the event happens and how it should be published.
											</p>
										</header>
										<div className="event-dialog__grid event-dialog__grid--three">
											<div className="admin-field">
												<span>Event Date *</span>
												<input
													type="date"
													value={formState.date}
													onChange={handleFieldChange("date")}
												/>
												{formErrors.date ? (
													<small className="ev-field-error">{formErrors.date}</small>
												) : null}
											</div>
											<div className="admin-field">
												<span>Event Time *</span>
												<input
													type="time"
													value={formState.time}
													onChange={handleFieldChange("time")}
												/>
												{formErrors.time ? (
													<small className="ev-field-error">{formErrors.time}</small>
												) : null}
											</div>
											<div className="admin-field">
												<span>Status *</span>
												<select
													value={formState.status}
													onChange={handleFieldChange("status")}
												>
													{STATUS_OPTIONS.map((option) => (
														<option key={option} value={option}>
															{option}
														</option>
													))}
												</select>
												{formErrors.status ? (
													<small className="ev-field-error">{formErrors.status}</small>
												) : null}
											</div>
										</div>
									</section>

									<section className="event-dialog__section">
										<header className="event-dialog__section-header">
											<p className="event-dialog__section-eyebrow">Logistics</p>
											<p className="event-dialog__section-subtitle">
												Outline where the event takes place and how many seats are available.
											</p>
										</header>
										<div className="event-dialog__grid event-dialog__grid--two">
											<div className="admin-field">
												<span>Venue/Location *</span>
												<input
													type="text"
													placeholder="Complete venue address or location details"
													value={formState.venue}
													onChange={handleFieldChange("venue")}
												/>
												{formErrors.venue ? (
													<small className="ev-field-error">{formErrors.venue}</small>
												) : null}
											</div>
											<div className="admin-field">
												<span>Capacity</span>
												<input
													type="number"
													min="0"
													placeholder="Optional"
													value={formState.capacity}
													onChange={handleFieldChange("capacity")}
												/>
												{formErrors.capacity ? (
													<small className="ev-field-error">{formErrors.capacity}</small>
												) : (
													<small>Leave blank to keep capacity flexible.</small>
												)}
											</div>
										</div>
										<div className="event-dialog__upload">
											<div className="admin-field">
												<span>Event Thumbnail Image *</span>
												<input
													key={`event-preview-input-${previewInputKey}`}
													type="file"
													accept={ACCEPTED_IMAGE_EXTENSIONS}
													onChange={handlePreviewImageSelect}
													disabled={isImageProcessing || isSubmitting || isDeleting}
												/>
												{formErrors.previewImage ? (
													<small className="ev-field-error">{formErrors.previewImage}</small>
												) : (
													<small>
														Upload JPG, JPEG, PNG, or WEBP (max 5MB before compression, 1MB upload target). {formState.previewImageFile
															? `Selected: ${formState.previewImageFile.name}`
															: formState.previewImageUrl
															? "Current image will remain if no new file is uploaded."
															: ""
														}
													</small>
												)}
												{isImageProcessing ? (
													<small className="admin-muted">Compressing image...</small>
												) : null}
											</div>
											{formState.previewImageFile ? (
												<div className="event-dialog__upload-actions">
													<button
														type="button"
														className="admin-button admin-button--ghost"
														onClick={handleClearPreviewImage}
													>
														<X size={14} />
														Clear selected image
													</button>
												</div>
											) : formState.previewImageUrl ? (
												<div className="event-dialog__upload-actions">
													<button
														type="button"
														className="admin-button admin-button--ghost"
														onClick={handleRemoveExistingPreview}
													>
														<X size={14} />
														Remove current image
													</button>
												</div>
											) : null}
										</div>
									</section>

									<section className="event-dialog__section">
										<header className="event-dialog__section-header">
											<p className="event-dialog__section-eyebrow">Registration redirect</p>
											<p className="event-dialog__section-subtitle">
												Enable if members should be sent to an external registration page.
											</p>
										</header>
										<div
											className={`event-dialog__toggle-card ${
												formState.externalLinkEnabled ? "is-active" : ""
											}`}
										>
											<div className="event-dialog__toggle-header">
												<div>
													<p
														id="external-link-redirect-heading"
														className="event-dialog__toggle-title"
													>
														External link redirect
													</p>
													<p
														id="external-link-redirect-helper"
														className="event-dialog__toggle-helper"
													>
														Members will open this URL instead of the in-app registration form.
													</p>
												</div>
												<label className="admin-switch" htmlFor="toggle-external-link">
													<input
														id="toggle-external-link"
														type="checkbox"
														checked={formState.externalLinkEnabled}
														onChange={handleExternalToggle}
														aria-labelledby="external-link-redirect-heading external-link-redirect-helper"
													/>
													<span className="admin-switch__track" aria-hidden="true" />
												</label>
											</div>
											<div className="event-dialog__toggle-body">
												<div className="admin-field">
													<span>External Link URL</span>
													<input
														type="url"
														placeholder="https://registration.example.com"
														value={formState.externalLink}
														onChange={handleFieldChange("externalLink")}
														onFocus={enableExternalLink}
													/>
													{formState.externalLinkEnabled && formErrors.externalLink ? (
														<small className="ev-field-error">
															{formErrors.externalLink}
														</small>
													) : (
														<small>
															Users will be redirected to this URL when they tap the event card.
														</small>
													)}
												</div>
											</div>
										</div>
									</section>
								</div>
							</div>

							<div className="admin-dialog__footer">
								{dialogState.mode === "edit" ? (
									<button
										type="button"
										className="admin-button is-danger ev-footer-delete"
										onClick={openDeleteConfirm}
										disabled={isSubmitting || isDeleting}
									>
										{isDeleting ? "Deleting…" : "Delete event"}
									</button>
								) : null}
								<button
									type="button"
									className="admin-button admin-button--ghost"
									onClick={closeDialog}
									disabled={isSubmitting || isDeleting}
								>
									Cancel
								</button>
								<button
									type="submit"
									className="admin-button is-primary"
									disabled={isSubmitting || isDeleting || isImageProcessing}
								>
									{isImageProcessing
										? "Compressing..."
										: isSubmitting
										? "Uploading..."
										: dialogState.mode === "create"
										? "Create event"
										: "Save changes"}
								</button>
							</div>
						</form>
					</div>
				</div>
			) : null}
		{confirmDeleteState.isOpen ? (
			<div className="admin-dialog" role="dialog" aria-modal="true">
				<div
					className="admin-dialog__backdrop"
					onClick={closeDeleteConfirm}
					role="presentation"
					style={{ cursor: isDeleting ? "not-allowed" : "pointer" }}
				/>
				<div className="admin-dialog__panel ev-delete-panel">
					<div className="admin-dialog__header">
						<div className="ev-compact-stack">
							<h3>Delete event</h3>
							<p className="admin-muted">
								Removing this event hides it from the directory and mobile preview for all members.
							</p>
						</div>
						<button
							type="button"
							className="admin-button admin-button--ghost"
							onClick={closeDeleteConfirm}
							disabled={isDeleting}
							aria-label="Close delete confirmation"
						>
							<X size={14} />
						</button>
					</div>
					<div className="admin-dialog__body admin-stack-md">
						<div className="ev-delete-warning">
							<AlertTriangle size={20} color="var(--admin-error-inline)" aria-hidden="true" />
							<div className="ev-delete-warning__content">
								<strong>{confirmDeleteState.event?.title || "Untitled event"}</strong>
								<p>
									Attendee access, schedule, and previews will no longer be available once deleted.
								</p>
							</div>
						</div>
						{dialogError ? (
							<div className="ev-dialog-error ev-dialog-error--danger">
								{dialogError}
							</div>
						) : null}
					</div>
					<div className="admin-dialog__footer">
						<button
							type="button"
							className="admin-button admin-button--ghost"
							onClick={closeDeleteConfirm}
							disabled={isDeleting}
						>
							Cancel
						</button>
						<button
							type="button"
							className="admin-button is-danger"
							onClick={handleDelete}
							disabled={isDeleting}
						>
							{isDeleting ? "Deleting…" : "Delete event"}
						</button>
					</div>
				</div>
			</div>
		) : null}
		</div>
	);
}
